import React, { useCallback, useId, useMemo } from "react";
import { Form, useFormik, FormikProvider } from "formik";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { getDate, getDay, getMonth, parseISO, subDays } from "date-fns";
import { StandardModalHeader } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import { useOverlayContainer } from "@design-system/overlay/OverlayContainerContext";
import {
    fullDateLongFormat,
    getUTCDateForPST,
    toIsoDate,
} from "@common/helpers/date";
import {
    ManualJournalRecurringPeriod,
    MonthRecurrenceType,
} from "@common/schemas/domains/accounting/manualJournalRecurringRule";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { generateDayOptions } from "@components/accounting/journalEntryDrawer/helpers";
import {
    weekDayOptions,
    monthOptions,
    monthRecurrenceTypeOptions,
    dayOptions,
    RECURRING_PERIOD,
} from "@components/accounting/journalEntryDrawer/constants";
import { useScopeSwitch } from "@hooks/keyboard/useScopeSwitch";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { CountInput } from "@design-system/CountInput/CountInput";
import { pluralize } from "@common/helpers/string";
import classNames from "classnames";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./JournalEntryRecurrenceSelect.module.scss";

export interface RecurrenceFormValues {
    interval?: number;
    period?: ManualJournalRecurringPeriod;
    weekDay?: number | null;
    month?: number | null;
    dateOfMonth?: number | null;
    monthRecurrenceType?: MonthRecurrenceType | null;
    endDate?: string | null;
    maxOccurrenceCount?: number | null;
}

const fields = createFormikFields<RecurrenceFormValues>();

type EndsMode = "after" | "never" | "on";

const QUICK_DAY_OF_MONTH = new Map([
    ["quick-first", 1],
    ["quick-15th", 15],
    ["quick-last", 31],
]);

const DAY_OF_MONTH_SECTIONS: Array<PickerSectionData<string | number>> = [
    {
        title: "Quick select",
        items: [
            { value: "quick-first", label: "First" },
            { value: "quick-15th", label: "15th" },
            { value: "quick-last", label: "Last" },
        ],
    },
    { title: "All days", items: dayOptions },
];

interface Props {
    initialValues: RecurrenceFormValues;
    disabled?: boolean;
    journalEntryDate: string;
    onSave: (values: RecurrenceFormValues) => void;
    onCancel: () => void;
}

export const RecurrenceModalContent: React.FC<Props> = ({
    initialValues,
    disabled,
    journalEntryDate,
    onSave,
    onCancel,
}) => {
    useScopeSwitch(HOTKEYS_SCOPES.GENERAL_MODAL);
    const { container: overlayContainer, containerRef } = useOverlayContainer();
    const selectPortalContainer =
        overlayContainer ?? containerRef?.current ?? undefined;
    const endMinDate = useMemo(() => {
        if (!journalEntryDate) {
            return undefined;
        }

        return toIsoDate(subDays(parseISO(journalEntryDate), 1));
    }, [journalEntryDate]);

    const formik = useFormik<RecurrenceFormValues>({
        initialValues,
        onSubmit: onSave,
    });
    const intervalLabelId = useId();

    const handlePeriodChange = useCallback(
        (value: ManualJournalRecurringPeriod) => {
            formik.setFieldValue("period", value);

            const currentDate = getUTCDateForPST(new Date(journalEntryDate));

            if (
                value !== ManualJournalRecurringPeriod.WEEKLY &&
                value !== ManualJournalRecurringPeriod.MONTHLY
            ) {
                formik.setFieldValue("weekDay", null);
            }

            if (
                value !== ManualJournalRecurringPeriod.MONTHLY &&
                value !== ManualJournalRecurringPeriod.YEARLY
            ) {
                formik.setFieldValue("dateOfMonth", null);
            }

            if (
                value === ManualJournalRecurringPeriod.WEEKLY &&
                !formik.values.weekDay
            ) {
                formik.setFieldValue("weekDay", getDay(currentDate));
            }

            if (value !== ManualJournalRecurringPeriod.MONTHLY) {
                formik.setFieldValue("monthRecurrenceType", null);
            } else if (!formik.values.monthRecurrenceType) {
                formik.setFieldValue(
                    "monthRecurrenceType",
                    MonthRecurrenceType.DAY,
                );
                formik.setFieldValue("dateOfMonth", getDate(currentDate));
            }

            if (value !== ManualJournalRecurringPeriod.YEARLY) {
                formik.setFieldValue("month", null);
            } else if (!formik.values.month) {
                formik.setFieldValue("month", getMonth(currentDate));
                formik.setFieldValue("dateOfMonth", getDate(currentDate));
            }
        },
        [formik, journalEntryDate],
    );

    const handleWeekDayChange = useCallback(
        (value: number) => {
            formik.setFieldValue("weekDay", value);
        },
        [formik],
    );

    const handleMonthChange = useCallback(
        (value: number) => {
            formik.setFieldValue("month", value);

            const daysInMonth = new Date(
                new Date().getFullYear(),
                value + 1,
                0,
            ).getDate();
            if (
                formik.values.dateOfMonth &&
                formik.values.dateOfMonth > daysInMonth
            ) {
                formik.setFieldValue("dateOfMonth", daysInMonth);
            }
        },
        [formik],
    );

    const handleDateOfMonthChange = useCallback(
        (value: number) => {
            formik.setFieldValue("dateOfMonth", value);
        },
        [formik],
    );

    const handleMonthRecurrenceTypeChange = useCallback(
        (value: MonthRecurrenceType) => {
            formik.setFieldValue("monthRecurrenceType", value);
            const currentDate = getUTCDateForPST(new Date(journalEntryDate));

            if (value === MonthRecurrenceType.DAY) {
                formik.setFieldValue("dateOfMonth", getDate(currentDate));
                formik.setFieldValue("weekDay", null);
            } else {
                formik.setFieldValue("dateOfMonth", null);
                if (!formik.values.weekDay) {
                    formik.setFieldValue("weekDay", getDay(currentDate));
                }
            }
        },
        [formik, journalEntryDate],
    );

    const handleChooseNever = useCallback(() => {
        formik.setFieldValue("endDate", null);
        formik.setFieldValue("maxOccurrenceCount", null);
    }, [formik]);
    const handleChooseEndDate = useCallback(() => {
        formik.setFieldValue("maxOccurrenceCount", null);
        if (!formik.values.endDate) {
            formik.setFieldValue("endDate", journalEntryDate);
        }
    }, [formik, journalEntryDate]);
    const handleChooseMaxOccurrence = useCallback(() => {
        formik.setFieldValue("endDate", null);
        if (!formik.values.maxOccurrenceCount) {
            formik.setFieldValue("maxOccurrenceCount", 1);
        }
    }, [formik]);

    let endsMode: EndsMode = "never";
    if (formik.values.endDate) {
        endsMode = "on";
    } else if (formik.values.maxOccurrenceCount) {
        endsMode = "after";
    }

    const handleEndsModeChange = useCallback(
        (value: EndsMode) => {
            if (value === "never") {
                handleChooseNever();
            } else if (value === "on") {
                handleChooseEndDate();
            } else {
                handleChooseMaxOccurrence();
            }
        },
        [handleChooseEndDate, handleChooseMaxOccurrence, handleChooseNever],
    );

    const yearlyDayOptions = useMemo(
        () => generateDayOptions(formik.values.month),
        [formik.values.month],
    );

    const customPeriodTriggerValue = useMemo(() => {
        const periodValue = formik.values.period;
        const intervalValue = formik.values.interval;
        if (!periodValue || !intervalValue) {
            return;
        }

        const chosenOption = RECURRING_PERIOD.find(
            ({ value }) => value === periodValue,
        );

        return (
            chosenOption &&
            pluralize(chosenOption.labelWithNumber, intervalValue)
        );
    }, [formik.values.period, formik.values.interval]);

    return (
        <FormikProvider value={formik}>
            <Form>
                <StandardModalHeader>Custom recurrence</StandardModalHeader>
                <div className={styles.block}>
                    <div className={styles.line}>
                        <div id={intervalLabelId} className={styles.leftLabel}>
                            Every
                        </div>

                        <div className={styles.fieldContainer}>
                            <fields.Count
                                name="interval"
                                aria-labelledby={intervalLabelId}
                                min={1}
                                className={styles.countField}
                            />

                            <Picker.Select
                                disabled={disabled}
                                container={selectPortalContainer}
                                value={formik.values.period ?? null}
                                onValueChange={(value) => {
                                    if (value != null) {
                                        handlePeriodChange(value);
                                    }
                                }}
                                items={RECURRING_PERIOD}
                                placeholder="Select"
                                renderValue={() =>
                                    customPeriodTriggerValue ?? "Select"
                                }
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="xl"
                                        className={styles.periodField}
                                    />
                                }
                            />
                        </div>
                    </div>

                    {formik.values.period ===
                        ManualJournalRecurringPeriod.WEEKLY && (
                        <div className={styles.line}>
                            <div className={styles.leftLabel}>On</div>
                            <div className={styles.fieldContainer}>
                                <Picker.Select
                                    disabled={disabled}
                                    container={selectPortalContainer}
                                    value={formik.values.weekDay ?? null}
                                    onValueChange={(value) => {
                                        if (value != null) {
                                            handleWeekDayChange(value);
                                        }
                                    }}
                                    items={weekDayOptions}
                                    placeholder="Select"
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="xl"
                                            className={classNames(
                                                styles.periodField,
                                                styles.weekDaySelect,
                                            )}
                                        />
                                    }
                                />
                            </div>
                        </div>
                    )}

                    {formik.values.period ===
                        ManualJournalRecurringPeriod.MONTHLY && (
                        <>
                            <div className={styles.line}>
                                <div className={styles.leftLabel}>On</div>
                                <div className={styles.fieldContainer}>
                                    <Picker.Select
                                        disabled={disabled}
                                        container={selectPortalContainer}
                                        value={
                                            formik.values.monthRecurrenceType ??
                                            null
                                        }
                                        onValueChange={(value) => {
                                            if (value != null) {
                                                handleMonthRecurrenceTypeChange(
                                                    value,
                                                );
                                            }
                                        }}
                                        items={monthRecurrenceTypeOptions}
                                        placeholder="Select type"
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                                className={styles.periodField}
                                            />
                                        }
                                    />

                                    {formik.values.monthRecurrenceType ===
                                        MonthRecurrenceType.DAY && (
                                        <Picker.Select
                                            disabled={disabled}
                                            container={selectPortalContainer}
                                            value={
                                                formik.values.dateOfMonth ??
                                                null
                                            }
                                            onValueChange={(v) => {
                                                const day =
                                                    typeof v === "string"
                                                        ? QUICK_DAY_OF_MONTH.get(
                                                              v,
                                                          )
                                                        : v;
                                                if (
                                                    day !== undefined &&
                                                    day !== null
                                                ) {
                                                    handleDateOfMonthChange(
                                                        day,
                                                    );
                                                }
                                            }}
                                            sections={DAY_OF_MONTH_SECTIONS}
                                            placeholder="Select"
                                            trigger={
                                                <Picker.Button
                                                    variant="primary"
                                                    size="xl"
                                                    className={
                                                        styles.periodField
                                                    }
                                                />
                                            }
                                        />
                                    )}
                                    {(formik.values.monthRecurrenceType ===
                                        MonthRecurrenceType.FIRST_WEEKDAY_OCCURRENCE ||
                                        formik.values.monthRecurrenceType ===
                                            MonthRecurrenceType.SECOND_WEEKDAY_OCCURRENCE ||
                                        formik.values.monthRecurrenceType ===
                                            MonthRecurrenceType.THIRD_WEEKDAY_OCCURRENCE ||
                                        formik.values.monthRecurrenceType ===
                                            MonthRecurrenceType.FOURTH_WEEKDAY_OCCURRENCE ||
                                        formik.values.monthRecurrenceType ===
                                            MonthRecurrenceType.LAST_WEEKDAY_OCCURRENCE) && (
                                        <Picker.Select
                                            disabled={disabled}
                                            container={selectPortalContainer}
                                            value={
                                                formik.values.weekDay ?? null
                                            }
                                            onValueChange={(v) => {
                                                if (v !== null) {
                                                    handleWeekDayChange(v);
                                                }
                                            }}
                                            items={weekDayOptions}
                                            placeholder="Select day of week"
                                            trigger={
                                                <Picker.Button
                                                    variant="primary"
                                                    size="xl"
                                                    className={classNames(
                                                        styles.periodField,
                                                        styles.weekDaySelect,
                                                    )}
                                                />
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {formik.values.period ===
                        ManualJournalRecurringPeriod.YEARLY && (
                        <>
                            <div className={styles.line}>
                                <div className={styles.leftLabel}>On</div>
                                <div className={styles.fieldContainer}>
                                    <Picker.Select
                                        disabled={disabled}
                                        container={selectPortalContainer}
                                        value={formik.values.month ?? null}
                                        onValueChange={(v) => {
                                            if (v !== null) {
                                                handleMonthChange(v);
                                            }
                                        }}
                                        items={monthOptions}
                                        placeholder="Select month"
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                                className={classNames(
                                                    styles.periodField,
                                                    styles.monthDaySelect,
                                                )}
                                            />
                                        }
                                    />
                                    <Picker.Select
                                        disabled={disabled}
                                        container={selectPortalContainer}
                                        value={
                                            formik.values.dateOfMonth ?? null
                                        }
                                        onValueChange={(v) => {
                                            if (v !== null) {
                                                handleDateOfMonthChange(v);
                                            }
                                        }}
                                        items={yearlyDayOptions}
                                        placeholder="Select day"
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                                className={styles.periodField}
                                            />
                                        }
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <h5 className="font-weight-medium text-prominent">Ends</h5>
                <RadioGroup<EndsMode>
                    name="end-date-option"
                    aria-label="Ends"
                    value={endsMode}
                    onValueChange={handleEndsModeChange}
                    className={styles.block}
                >
                    <div className={styles.line}>
                        <div className={styles.leftLabel}>
                            <Radio value="never" label="Never" />
                        </div>

                        <div className={styles.fieldContainer}>
                            <span className={styles.neverDescription}>
                                Future entries will be created 1 day in advance.
                            </span>
                        </div>
                    </div>

                    <div className={styles.line}>
                        <div className={styles.leftLabel}>
                            <Radio value="on" label="On" />
                        </div>

                        <fields.Date
                            name="endDate"
                            label="End date"
                            labelHidden
                            className={styles.endDateContainer}
                            variant="panel-input"
                            minDate={endMinDate}
                            displayFormat={fullDateLongFormat}
                            disabled={!formik.values.endDate}
                            buttonProps={{
                                variant: "primary",
                                size: "xl",
                                block: true,
                            }}
                        />
                    </div>

                    <div className={styles.line}>
                        <div className={styles.leftLabel}>
                            <Radio value="after" label="After" />
                        </div>

                        <div className={styles.fieldContainer}>
                            <CountInput
                                disabled={!formik.values.maxOccurrenceCount}
                                min={1}
                                value={formik.values.maxOccurrenceCount ?? 1}
                                onValueChange={(value) =>
                                    formik.setFieldValue(
                                        "maxOccurrenceCount",
                                        value,
                                    )
                                }
                                className={styles.countField}
                            />

                            <span
                                className={classNames(styles.occurrenceSuffix, {
                                    [styles.occurrenceDisabled]:
                                        !formik.values.maxOccurrenceCount,
                                })}
                            >
                                {pluralize(
                                    "occurrence",
                                    formik.values.maxOccurrenceCount ?? 1,
                                )}
                            </span>
                        </div>
                    </div>
                </RadioGroup>

                <HStack as="footer" justify="end" gap={12} className="mt-4">
                    <Button variant="secondary" size="xl" onClick={onCancel}>
                        Cancel
                    </Button>
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Save",
                            scopes: [HOTKEYS_SCOPES.GENERAL_MODAL],
                        }}
                    >
                        <Button
                            disabled={
                                disabled ||
                                !formik.values.interval ||
                                !formik.values.period
                            }
                            onClick={() => formik.handleSubmit()}
                            type="submit"
                            variant="primary"
                            size="xl"
                        >
                            Save
                        </Button>
                    </WithHotkey>
                </HStack>
            </Form>
        </FormikProvider>
    );
};
